import {
  ConditionType,
  AmountOperator,
  CounterpartyOperator,
  ExpenseOperator
} from 'openapi';

export interface ConditionTypeOption {
  value: ConditionType;
  labelKey: string;
}

export interface ConditionOperatorOption {
  value: AmountOperator | CounterpartyOperator | ExpenseOperator;
  labelKey: string;
}

export const CONDITION_TYPE_OPTIONS: readonly ConditionTypeOption[] = [
  { value: ConditionType.EXPENSE, labelKey: 'approvals.expenseType' },
  { value: ConditionType.AMOUNT, labelKey: 'approvals.amount' },
  { value: ConditionType.COUNTERPARTY, labelKey: 'approvals.counterparty' }
] as const;

export const CONDITION_OPERATOR_OPTIONS: Record<
  ConditionType,
  readonly ConditionOperatorOption[]
> = {
  [ConditionType.EXPENSE]: [
    { value: ExpenseOperator.IS, labelKey: 'approvals.is' },
    { value: ExpenseOperator.IS_NOT, labelKey: 'approvals.isNot' },
    { value: ExpenseOperator.IS_NONE_OF, labelKey: 'approvals.isNoneOf' },
    { value: ExpenseOperator.IS_ANY_OF, labelKey: 'approvals.isAnyOf' }
  ],
  [ConditionType.AMOUNT]: [
    { value: AmountOperator.LESS_THAN, labelKey: 'approvals.isLessThan' },
    {
      value: AmountOperator.LESS_THAN_OR_EQUAL,
      labelKey: 'approvals.isLessThanOrEqualTo'
    },
    { value: AmountOperator.GREATER_THAN, labelKey: 'approvals.isGreaterThan' },
    {
      value: AmountOperator.GREATER_THAN_OR_EQUAL,
      labelKey: 'approvals.isGreaterThanOrEqualTo'
    },
    { value: AmountOperator.EQUALS, labelKey: 'approvals.equals' },
    { value: AmountOperator.BETWEEN, labelKey: 'approvals.isBetween' }
  ],
  [ConditionType.COUNTERPARTY]: [
    { value: CounterpartyOperator.IS, labelKey: 'approvals.is' },
    { value: CounterpartyOperator.IS_ANY_OF, labelKey: 'approvals.isAnyOf' },
    {
      value: CounterpartyOperator.IS_VALIDATED,
      labelKey: 'approvals.isValidated'
    },
    {
      value: CounterpartyOperator.IS_NOT_VALIDATED,
      labelKey: 'approvals.isNotValidated'
    }
  ]
} as const;

export const getConditionOperators = (
  conditionType: ConditionType
): readonly ConditionOperatorOption[] => {
  return CONDITION_OPERATOR_OPTIONS[conditionType] || [];
};

export const formatOperatorForPreview = (
  operator: ExpenseOperator | AmountOperator | CounterpartyOperator,
  translate: (key: string) => string
): string => {
  switch (operator) {
    case ExpenseOperator.IS:
    case CounterpartyOperator.IS:
      return translate('approvals.is');
    case ExpenseOperator.IS_NOT:
      return translate('approvals.isNot');
    case ExpenseOperator.IS_ANY_OF:
      return translate('approvals.isAnyOf');
    case ExpenseOperator.IS_NONE_OF:
      return translate('approvals.isNoneOf');
    case AmountOperator.LESS_THAN:
      return translate('approvals.isLessThan');
    case AmountOperator.LESS_THAN_OR_EQUAL:
      return translate('approvals.isLessThanOrEqualTo');
    case AmountOperator.GREATER_THAN:
      return translate('approvals.isGreaterThan');
    case AmountOperator.GREATER_THAN_OR_EQUAL:
      return translate('approvals.isGreaterThanOrEqualTo');
    case AmountOperator.EQUALS:
      return translate('approvals.equals');
    case AmountOperator.BETWEEN:
      return translate('approvals.isBetween');
    case CounterpartyOperator.IS_ANY_OF:
      return translate('approvals.isAnyOf');
    case CounterpartyOperator.IS_VALIDATED:
      return translate('approvals.isValidated');
    case CounterpartyOperator.IS_NOT_VALIDATED:
      return translate('approvals.isNotValidated');
    default:
      return translate('approvals.is');
  }
};
